import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api } from '../../lib/api';
import { Card, CardHeader, Badge, ActionButton, Spinner, EmptyState } from '../ui';
import { ModelTree, computeNodeValues } from '../ModelTree';
import { fromInputValue, type DriverUnit } from '../../lib/fpaDrivers';

/** The driver → line → margin → profit/cash model map, with an in-memory
 *  what-if (edit a driver, Run). Read/explore surface in the Review section. */
export function ModelTab({ planId, canWrite }: { planId: string; canWrite: boolean }) {
  const { data, isLoading } = useQuery(() => api.fpa.plans.modelGraph(planId), [planId]);
  const depts = useQuery(() => api.fpa.departments());
  const [overrides, setOverrides] = useState<Map<string, string>>(new Map());
  const [scenarioValues, setScenarioValues] = useState<Map<string, number> | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hasValues = (data?.nodes ?? []).some((n) => n.value != null);
  const dirty = overrides.size > 0;

  function setOverride(code: string, value: string) {
    setOverrides((m) => { const n = new Map(m); if (value.trim() === '') n.delete(code); else n.set(code, value); return n; });
    setScenarioValues(null);
  }
  function reset() { setOverrides(new Map()); setScenarioValues(null); setErr(null); }

  async function run() {
    if (!data) return;
    setErr(null); setRunning(true);
    try {
      const nodeByCode = new Map(data.nodes.map((n) => [n.code, n]));
      const assumption_overrides: Array<{ assumption_key: string; department_id: string | null; value_numeric: number | null }> = [];
      for (const [code, val] of overrides) {
        const node = nodeByCode.get(code); if (!node) continue;
        const num = fromInputValue(node.unit as DriverUnit, val);
        if (num === null) continue;
        assumption_overrides.push({ assumption_key: code, department_id: null, value_numeric: num });
        if (node.scope === 'department') {
          for (const d of depts.data?.departments ?? []) assumption_overrides.push({ assumption_key: code, department_id: d.id, value_numeric: num });
        }
      }
      const res = await api.fpa.plans.scenario(planId, { assumption_overrides });
      setScenarioValues(computeNodeValues(res.lines));
    } catch (e) { setErr((e as Error).message); }
    finally { setRunning(false); }
  }

  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Model map {scenarioValues && <Badge variant="amber">what-if</Badge>}</div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>drivers → lines → margins → profit &amp; cash · {canWrite ? 'edit a driver and Run what-if' : 'hover a node for its formula'}</span>
        {canWrite && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
            <ActionButton variant="primary" disabled={running || !dirty} onClick={run}>{running ? 'Running…' : 'Run what-if'}</ActionButton>
            {(dirty || scenarioValues) && <ActionButton onClick={reset}>Reset</ActionButton>}
          </span>
        )}
      </CardHeader>
      <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap', borderBottom: '0.5px solid var(--color-border)' }}>
        <span><span style={{ color: 'var(--color-blue)' }}>■</span> driver / margin</span>
        <span><span style={{ color: 'var(--color-green)' }}>■</span> revenue / cash</span>
        <span><span style={{ color: 'var(--color-amber)' }}>■</span> cost</span>
        <span><span style={{ color: 'var(--color-text-muted)' }}>■</span> custom formula</span>
        {scenarioValues && <span style={{ marginLeft: 'auto', color: 'var(--color-amber-text)' }}>showing what-if vs baseline (in-memory, not saved)</span>}
        {err && <span style={{ color: 'var(--color-red-text)' }}>{err}</span>}
      </div>
      {isLoading ? <Spinner />
        : !data ? <EmptyState message="No model graph." />
        : (
          <>
            {!hasValues && <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>Values appear after you Recompute the plan. The structure shows regardless.</div>}
            <ModelTree graph={data} editable={canWrite} overrides={overrides} scenarioValues={scenarioValues} onOverrideChange={setOverride} />
          </>
        )}
    </Card>
  );
}
